/*Import Estilo*/
import './index.scss';
import '../../../../css/global.css';
import '../../../../css/normalize.css';

/*Import Bibliotecas*/
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/*Import Componentes de Gráficos*/
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar
} from 'recharts';

/*Import Componentes*/
import Cabecalho from '../../../../components/Cabecalho';
import Rodape from '../../../../components/Rodape';
import CookieAceite from '../../../../components/CookieAceite';

/*Import Requisição API*/
import api from '../../../../api';
import MenuLateral from '../../../../components/MenuLateral';

export default function Financeiro() {
    const [texto, setTexto] = useState('');
    const navigate = useNavigate();
    const [exportando, setExportando] = useState(false);

    const caixaDeDialogo = useRef(null);

    useEffect(() => {
        caixaDeDialogo.current = document.getElementById('CaixaDeDialogo');
    }, []);

    const fecharModal = () => {
        if (caixaDeDialogo.current) {
            caixaDeDialogo.current.close();
        }
    };

    const mostrarModal = (mensagem = '') => {
        setTexto(mensagem);
        if (caixaDeDialogo.current) {
            caixaDeDialogo.current.showModal();
        }
    };

    const meses = [
        { id: '01', label: 'Janeiro' },
        { id: '02', label: 'Fevereiro' },
        { id: '03', label: 'Março' },
        { id: '04', label: 'Abril' },
        { id: '05', label: 'Maio' },
        { id: '06', label: 'Junho' },
        { id: '07', label: 'Julho' },
        { id: '08', label: 'Agosto' },
        { id: '09', label: 'Setembro' },
        { id: '10', label: 'Outubro' },
        { id: '11', label: 'Novembro' },
        { id: '12', label: 'Dezembro' }
    ];
    const anoAtual = new Date().getFullYear();
    const anos = Array.from({ length: 6 }, (_, idx) => String(anoAtual - idx));
    const [mesSelecionado, setMesSelecionado] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
    const [anoSelecionado, setAnoSelecionado] = useState(String(anoAtual));

    // Estados para os dados financeiros
    const [resumoFinanceiro, setResumoFinanceiro] = useState({
        totalVendas: 0,
        totalPedidos: 0,
        pagamentosPendentes: 0,
        totalRecebido: 0
    });
    const [lucroTotal, setLucroTotal] = useState(0); // Novo estado para lucros
    const [pedidos, setPedidos] = useState([]);
    const [dadosVendasMensais, setDadosVendasMensais] = useState([]); // Para gráfico de linha
    const [dadosPagamentosStatus, setDadosPagamentosStatus] = useState([]); // Para gráfico de barras (ex: contagem por status)
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [buscaPedido, setBuscaPedido] = useState('');
    const [filtroStatusFinanceiro, setFiltroStatusFinanceiro] = useState('todos');

    const extrairListaPedidos = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.pedidos)) return payload.pedidos;
        return [];
    };

    // Função para buscar o resumo financeiro (KPIs)
    function gerarDatas(mes, ano) {
        const anoNum = Number(ano);
        const mesNum = Number(mes);

        const start = `${anoNum}-${String(mesNum).padStart(2, '0')}-01`;
        const ultimoDia = new Date(anoNum, mesNum, 0).getDate();
        const end = `${anoNum}-${String(mesNum).padStart(2, '0')}-${ultimoDia}`;

        return { start, end };
    }

    const buscarResumoFinanceiro = useCallback(async (mes, ano) => {
        try {
            const { start, end } = gerarDatas(mes, ano);
            const response = await api.get(`/financeiro/resumo?start=${start}&end=${end}`);
            setResumoFinanceiro(response.data);
        } catch (error) {
            setErro('Erro ao carregar resumo financeiro: ' + error.message);
            mostrarModal('Erro ao carregar dados do resumo financeiro.');
        }
    }, []);

    // Nova função para buscar lucros
    const buscarLucros = useCallback(async (mes, ano) => {
        try {
            const { start, end } = gerarDatas(mes, ano);
            const response = await api.get(`/financeiro/lucros?start=${start}&end=${end}`);
            setLucroTotal(response.data.lucro || 0);
        } catch (error) {
            console.error('Erro ao carregar lucros:', error.message);
            setErro('Erro ao carregar lucros: ' + error.message);
            mostrarModal('Erro ao carregar dados de lucros.');
        }
    }, []);

    // Função para buscar dados mensais de vendas (para gráfico)
    function normalizarVendas(dados, periodoAtual) {
        if (!dados || !Array.isArray(dados)) return [];

        let processados = dados.map((item, index) => {
            const valor = Number(item.valor) || 0;
            let label = '';
            let orderKey = index;

            if (periodoAtual === 'ano' && item.mes) {
                const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                const mesNum = Number(item.mes);
                label = nomes[(mesNum - 1) % 12] || item.mes;
                orderKey = mesNum;
            } else if ((periodoAtual === 'mes' || periodoAtual === 'semana') && (item.dia || item.data)) {
                const dia = Number(item.dia || item.data) || 0;
                label = String(dia).padStart(2, '0');
                orderKey = dia;
            } else {
                label = item.label || item.mes || item.dia || `Item ${index + 1}`;
            }

            return { ...item, label, valor, orderKey };
        });

        return processados.sort((a, b) => a.orderKey - b.orderKey);
    }

    const buscarVendasMensais = useCallback(async (mes, ano) => {
        try {
            const { start, end } = gerarDatas(mes, ano);
            const response = await api.get(`/financeiro/vendas-mensais?start=${start}&end=${end}`);
            const dados = normalizarVendas(response.data.data || [], 'mes');
            setDadosVendasMensais(dados);
        } catch (error) {
            console.error('Erro ao carregar dados mensais:', error.message);
            setErro('Erro ao carregar dados mensais: ' + error.message);
            mostrarModal('Erro ao carregar dados de vendas mensais.');
        }
    }, []);

    // Função para buscar dados de status de pagamentos (para gráfico de barras)
    const buscarPagamentosStatus = useCallback(async (mes, ano) => {
        try {
            const { start, end } = gerarDatas(mes, ano);
            const response = await api.get(`/financeiro/pagamentos-status?start=${start}&end=${end}`);
            setDadosPagamentosStatus(response.data.data || []);
        } catch (error) {
            console.error('Erro ao carregar status de pagamentos:', error.message);
            setErro('Erro ao carregar status de pagamentos: ' + error.message);
            mostrarModal('Erro ao carregar dados de status de pagamentos.');
        }
    }, []);

    // Função para buscar lista de pedidos
    const buscarPedidos = useCallback(async (mes, ano) => {
        try {
            const { start, end } = gerarDatas(mes, ano);

            const responsePeriodo = await api.get(`/financeiro/pedidos?limit=50&start=${start}&end=${end}`);
            let listaPedidos = extrairListaPedidos(responsePeriodo.data);

            // Fallback: se o período não retornar itens, tenta a listagem geral
            // para não deixar a tabela do financeiro vazia.
            if (!listaPedidos.length) {
                const responseGeral = await api.get('/financeiro/pedidos?limit=50');
                listaPedidos = extrairListaPedidos(responseGeral.data);
            }

            setPedidos(listaPedidos);
        } catch (error) {
            setErro('Erro ao carregar pedidos: ' + error.message);
            mostrarModal('Erro ao carregar lista de pedidos.');
        }
    }, []);

    useEffect(() => {
        const carregarDados = async () => {
            setCarregando(true);
            setErro(null);
            try {
                await Promise.all([
                    buscarResumoFinanceiro(mesSelecionado, anoSelecionado),
                    buscarLucros(mesSelecionado, anoSelecionado),
                    buscarVendasMensais(mesSelecionado, anoSelecionado),
                    buscarPagamentosStatus(mesSelecionado, anoSelecionado),
                    buscarPedidos(mesSelecionado, anoSelecionado)
                ]);
            } finally {
                setCarregando(false);
            }
        };

        carregarDados();
    }, [
        mesSelecionado,
        anoSelecionado,
        buscarResumoFinanceiro,
        buscarLucros,
        buscarVendasMensais,
        buscarPagamentosStatus,
        buscarPedidos
    ]);

    // Função para formatar moeda brasileira
    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    const formatarMoedaCurta = (valor) => {
        const abs = Math.abs(valor || 0);
        if (abs >= 1000000) return `R$ ${(valor / 1000000).toFixed(1)}M`;
        if (abs >= 1000) return `R$ ${(valor / 1000).toFixed(1)}k`;
        return formatarMoeda(valor);
    };

    // Função para formatar data
    const formatarData = (dataString) => {
        return new Date(dataString).toLocaleDateString('pt-BR');
    };

    const CustomTooltipVendas = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        return (
            <div className="tooltip-grafico">
                <p className="tooltip-titulo">{label}</p>
                <p className="tooltip-valor">{formatarMoeda(payload[0].value)}</p>
            </div>
        );
    };

    const CustomTooltipStatus = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
        return (
            <div className="tooltip-grafico">
                <p className="tooltip-titulo">{label}</p>
                <p className="tooltip-valor">{payload[0].value} pagamentos</p>
            </div>
        );
    };

    const GraficoVazio = ({ texto }) => (
        <div className="placeholder-grafico">{texto}</div>
    );

    const handleViewPedido = (id) => {
        console.log(`Visualizar produto com ID: ${id}`);
        navigate('/menu/pedidos/visualizarpedido', { state: { id } });
    };

    const PRAZO_VENCIMENTO_DIAS = 30;

    const statusPagamentoNormalizado = (status = '') => String(status).trim().toLowerCase();

    const dataBasePedido = (pedido) => {
        return pedido?.data_vencimento || pedido?.data_entrega || pedido?.data_envio || pedido?.criado_em;
    };

    const isPedidoVencido = (pedido) => {
        const status = statusPagamentoNormalizado(pedido?.status_pagamento);
        if (status === 'pago' || status === 'cancelado') return false;

        const dataBase = dataBasePedido(pedido);
        if (!dataBase) return false;

        const agora = new Date();
        const dataPedido = new Date(dataBase);
        const diffDias = Math.floor((agora - dataPedido) / (1000 * 60 * 60 * 24));

        return diffDias > PRAZO_VENCIMENTO_DIAS;
    };

    const getCategoriaFinanceira = (pedido) => {
        const status = statusPagamentoNormalizado(pedido?.status_pagamento);

        if (status === 'pago') return 'pago';
        if (isPedidoVencido(pedido)) return 'vencido';
        return 'receber';
    };

    const pedidosEnriquecidos = (pedidos || []).map((pedido) => ({
        ...pedido,
        categoriaFinanceira: getCategoriaFinanceira(pedido)
    }));

    const contadoresPedidos = {
        pago: pedidosEnriquecidos.filter((pedido) => pedido.categoriaFinanceira === 'pago').length,
        receber: pedidosEnriquecidos.filter((pedido) => pedido.categoriaFinanceira === 'receber').length,
        vencido: pedidosEnriquecidos.filter((pedido) => pedido.categoriaFinanceira === 'vencido').length
    };

    const pedidosFiltrados = pedidosEnriquecidos.filter((pedido) => {
        const bateFiltro = filtroStatusFinanceiro === 'todos'
            ? true
            : pedido.categoriaFinanceira === filtroStatusFinanceiro;

        if (!bateFiltro) return false;

        const busca = buscaPedido.trim().toLowerCase();
        if (!busca) return true;

        const camposPesquisa = [
            String(pedido.id || ''),
            String(pedido.nome_usuario || ''),
            String(pedido.status || ''),
            String(pedido.status_pagamento || '')
        ].join(' ').toLowerCase();

        return camposPesquisa.includes(busca);
    });

    const getBadgeLabel = (categoria) => {
        if (categoria === 'pago') return 'Pago';
        if (categoria === 'vencido') return 'Vencido';
        return 'A Receber';
    };

    const handleMesChange = (event) => {
        setMesSelecionado(event.target.value);
    };

    const handleAnoChange = (event) => {
        setAnoSelecionado(event.target.value);
    };

    const handleExport = async (formato = 'xlsx') => {
        setExportando(true);
        try {
            const { start, end } = gerarDatas(mesSelecionado, anoSelecionado);
            const response = await api.get(
                `/financeiro/export?start=${start}&end=${end}&formato=${formato}`,
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `relatorio-financeiro-${mesSelecionado}-${anoSelecionado}.${formato}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            mostrarModal('Relatório exportado com sucesso!');
        } catch (error) {
            console.error('Erro ao exportar:', error);
            mostrarModal('Erro ao exportar relatório. Tente novamente.');
        } finally {
            setExportando(false);
        }
    };

    if (carregando) {
        return (
            <section className='container-principal-financeiro'>
                <Cabecalho />
                <CookieAceite />
                <main>
                    <MenuLateral />
                    <section className='conteudo-financeiro'>
                        <div className='titulo-financeiro'><h1>Dashboard Financeiro</h1></div>
                        <div className='loading'>
                            <svg xmlns='http://www.w3.org/2000/svg' width='25' height='25' viewBox='0 0 24 24'><path fill='#131C46' d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z' opacity='0.25' /><path fill='#131C46' d='M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z'><animateTransform attributeName='transform' dur='0.75s' repeatCount='indefinite' type='rotate' values='0 12 12;360 12 12' /></path></svg>
                            <h2>Carregando dados financeiros...</h2>
                        </div>
                    </section>
                </main>
                <Rodape />
            </section>
        );
    }

    return (
        <section className='container-principal-financeiro'>
            <dialog id="CaixaDeDialogo">
                <div className="conteudo-dialog">
                    <div className="dialog-texto">
                        <p>{texto}</p>
                        <button onClick={fecharModal}>Ok</button>
                    </div>
                </div>
            </dialog>
            <Cabecalho />
            <CookieAceite />

            <main>
                <MenuLateral />

                <section className='conteudo-financeiro'>
                    <div className='titulo-financeiro'>
                        <h1>Dashboard Financeiro</h1>
                        {erro && <p className="erro">{erro}</p>}
                    </div>

                    <div className="filtros-bloco">
                        <div className="filtros-data">
                            <span className="filtro-titulo">Período</span>
                            <div className="filtros-selects">
                                <label>
                                    Mês
                                    <select value={mesSelecionado} onChange={handleMesChange}>
                                        {meses.map((mes) => (
                                            <option key={mes.id} value={mes.id}>{mes.label}</option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    Ano
                                    <select value={anoSelecionado} onChange={handleAnoChange}>
                                        {anos.map((ano) => (
                                            <option key={ano} value={ano}>{ano}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className="filtros-export">
                            <button
                                className="btn-export"
                                onClick={() => handleExport('xlsx')}
                                disabled={exportando}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7V12C2 16.5 8 22 12 22C16 22 22 16.5 22 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M12 11V17M9 14H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                {exportando ? 'Exportando...' : 'Exportar Excel'}
                            </button>
                        </div>
                    </div>

                    {/* KPIs - Indicadores Chave de Desempenho */}
                    <div className="kpis">
                        <div className="kpi-card">
                            <h3>Total de Vendas</h3>
                            <p className="valor">{formatarMoeda(resumoFinanceiro.totalVendas)}</p>
                        </div>
                        <div className="kpi-card">
                            <h3>Total de Pedidos</h3>
                            <p className="valor">{resumoFinanceiro.totalPedidos}</p>
                        </div>
                        <div className="kpi-card">
                            <h3>Pagamentos Pendentes</h3>
                            <p className="valor">{resumoFinanceiro.pagamentosPendentes}</p>
                        </div>
                        <div className="kpi-card">
                            <h3>Total Recebido</h3>
                            <p className="valor">{formatarMoeda(resumoFinanceiro.totalRecebido)}</p>
                        </div>
                        <div className="kpi-card">
                            <h3>Lucro Total</h3>
                            <p className="valor">{formatarMoeda(lucroTotal)}</p>
                        </div>
                    </div>

                    {/* Seção de Gráficos */}
                    <div className="secao-graficos">
                        {/* Gráfico de Linha: Vendas Mensais */}
                        <div className="grafico">
                            <h3>Vendas por Mês</h3>
                            {dadosVendasMensais && dadosVendasMensais.length ? (
                                <ResponsiveContainer height={320}>
                                    <LineChart data={dadosVendasMensais} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#131C46" stopOpacity={0.25} />
                                                <stop offset="100%" stopColor="#131C46" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#d9d9d9" />
                                        <XAxis dataKey="mes" tickMargin={8} />
                                        <YAxis
                                            tickFormatter={formatarMoedaCurta}
                                            width={90}
                                            domain={[0, (dataMax) => Math.ceil((dataMax || 100) * 1.3)]}
                                        />
                                        <Tooltip content={<CustomTooltipVendas />} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="valor"
                                            stroke="#131C46"
                                            strokeWidth={3}
                                            dot={{ r: 5, fill: '#fff', stroke: '#131C46', strokeWidth: 3 }}
                                            activeDot={{ r: 7, stroke: '#FFC300', strokeWidth: 3 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <GraficoVazio texto="Sem dados de vendas ainda." />
                            )}
                        </div>

                        {/* Gráfico de Barras: Status de Pagamentos */}
                        <div className="grafico">
                            <h3>Distribuição de Status de Pagamentos</h3>
                            {dadosPagamentosStatus && dadosPagamentosStatus.length ? (
                                <ResponsiveContainer height={320}>
                                    <BarChart data={dadosPagamentosStatus} margin={{ top: 10, right: 16, left: 0, bottom: 0 }} barCategoryGap="12%">
                                        <defs>
                                            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#FFC300" stopOpacity={0.9} />
                                                <stop offset="100%" stopColor="#FFC300" stopOpacity={0.6} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#d9d9d9" />
                                        <XAxis dataKey="status" tickMargin={8} />
                                        <YAxis allowDecimals={false} width={60} />
                                        <Tooltip content={<CustomTooltipStatus />} />
                                        <Legend />
                                        <Bar dataKey="count" fill="url(#barGradient)" radius={[12, 12, 6, 6]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <GraficoVazio texto="Sem dados de pagamento ainda." />
                            )}
                        </div>
                    </div>

                    {/* Tabela Unificada de Pedidos Financeiros */}
                    <div className="tabela-pedidos">
                        <div className="topo-tabela-pedidos">
                            <h3>Pedidos Financeiros</h3>
                            <div className="chips-resumo-pedidos">
                                <span className="chip chip-receber">A Receber: {contadoresPedidos.receber}</span>
                                <span className="chip chip-vencido">Vencidos: {contadoresPedidos.vencido}</span>
                                <span className="chip chip-pago">Pagos: {contadoresPedidos.pago}</span>
                            </div>
                        </div>

                        <div className="filtros-pedidos-financeiro">
                            <div className='barraDePesquisa-Produtos'>
                                <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19.6667 19.6667L28 28M12.7222 22.4444C11.4455 22.4444 10.1812 22.193 9.00169 21.7044C7.82213 21.2158 6.75036 20.4997 5.84757 19.5969C4.94478 18.6941 4.22865 17.6223 3.74006 16.4428C3.25147 15.2632 3 13.999 3 12.7222C3 11.4455 3.25147 10.1812 3.74006 9.00169C4.22865 7.82213 4.94478 6.75036 5.84757 5.84757C6.75036 4.94478 7.82213 4.22865 9.00169 3.74006C10.1812 3.25147 11.4455 3 12.7222 3C15.3007 3 17.7736 4.0243 19.5969 5.84757C21.4201 7.67084 22.4444 10.1437 22.4444 12.7222C22.4444 15.3007 21.4201 17.7736 19.5969 19.5969C17.7736 21.4201 15.3007 22.4444 12.7222 22.4444Z" stroke="#000" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Pesquise o pedido por: ID, cliente ou status"
                                    value={buscaPedido}
                                    onChange={(e) => setBuscaPedido(e.target.value)}
                                />
                            </div>

                            <div className='filtro-status-financeiro-wrapper'>
                                <select
                                    className='filtro-status-financeiro'
                                    value={filtroStatusFinanceiro}
                                    onChange={(e) => setFiltroStatusFinanceiro(e.target.value)}
                                >
                                    <option value="todos">Todos ({pedidosEnriquecidos.length})</option>
                                    <option value="pago">Pagos ({contadoresPedidos.pago})</option>
                                    <option value="receber">A Receber ({contadoresPedidos.receber})</option>
                                    <option value="vencido">Vencidos ({contadoresPedidos.vencido})</option>
                                </select>
                            </div>
                        </div>

                        <div className="container-interno-produtos">
                            {pedidosFiltrados.length > 0 ? (
                                <table>
                                    <thead>
                                        <tr>
                                            <th>ID Pedido</th>
                                            <th>Cliente</th>
                                            <th>Total</th>
                                            <th>Status</th>
                                            <th>Data</th>
                                            <th>Status Pagamento</th>
                                            <th>Categoria</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pedidosFiltrados.map((pedido) => (
                                            <tr key={pedido.id} className="conteudo">
                                                <td className="primeiro">{pedido.id}</td>
                                                <td>{pedido.nome_usuario || 'N/A'}</td>
                                                <td>{formatarMoeda(pedido.total)}</td>
                                                <td>{pedido.status}</td>
                                                <td>{formatarData(pedido.criado_em)}</td>
                                                <td>{pedido.status_pagamento}</td>
                                                <td>
                                                    <span className={`badge-categoria ${pedido.categoriaFinanceira}`}>
                                                        {getBadgeLabel(pedido.categoriaFinanceira)}
                                                    </span>
                                                </td>
                                                <td className="final">
                                                    <div className="bnts-produtos">
                                                        <button className='bnt-visualizar' onClick={() => handleViewPedido(pedido.id)}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.4375 6.5625C8.4375 6.31527 8.51081 6.0736 8.64817 5.86804C8.78552 5.66248 8.98074 5.50226 9.20915 5.40765C9.43756 5.31304 9.68889 5.28829 9.93137 5.33652C10.1738 5.38475 10.3966 5.5038 10.5714 5.67862C10.7462 5.85343 10.8653 6.07616 10.9135 6.31864C10.9617 6.56111 10.937 6.81245 10.8424 7.04085C10.7477 7.26926 10.5875 7.46449 10.382 7.60184C10.1764 7.73919 9.93473 7.8125 9.6875 7.8125C9.35598 7.8125 9.03804 7.6808 8.80362 7.44638C8.5692 7.21196 8.4375 6.89402 8.4375 6.5625ZM18.4375 10C18.4375 11.6688 17.9427 13.3001 17.0155 14.6876C16.0884 16.0752 14.7706 17.1566 13.2289 17.7952C11.6871 18.4338 9.99064 18.6009 8.35393 18.2754C6.71721 17.9498 5.2138 17.1462 4.03379 15.9662C2.85379 14.7862 2.05019 13.2828 1.72463 11.6461C1.39907 10.0094 1.56616 8.31286 2.20477 6.77111C2.84338 5.22936 3.92484 3.9116 5.31238 2.98448C6.69992 2.05735 8.33122 1.5625 10 1.5625C12.237 1.56498 14.3817 2.45473 15.9635 4.03653C17.5453 5.61833 18.435 7.763 18.4375 10ZM16.5625 10C16.5625 8.70206 16.1776 7.43327 15.4565 6.35407C14.7354 5.27487 13.7105 4.43374 12.5114 3.93704C11.3122 3.44034 9.99272 3.31038 8.71972 3.5636C7.44672 3.81681 6.2774 4.44183 5.35962 5.35961C4.44183 6.27739 3.81682 7.44672 3.5636 8.71972C3.31039 9.99272 3.44034 11.3122 3.93704 12.5114C4.43374 13.7105 5.27488 14.7354 6.35407 15.4565C7.43327 16.1776 8.70206 16.5625 10 16.5625C11.7399 16.5606 13.408 15.8686 14.6383 14.6383C15.8686 13.408 16.5606 11.7399 16.5625 10ZM10.9375 12.8656V10.3125C10.9375 9.8981 10.7729 9.50067 10.4799 9.20765C10.1868 8.91462 9.7894 8.75 9.375 8.75C9.1536 8.74967 8.93923 8.82771 8.76986 8.97029C8.60048 9.11287 8.48703 9.3108 8.4496 9.52901C8.41217 9.74722 8.45318 9.97164 8.56536 10.1625C8.67754 10.3534 8.85365 10.4984 9.0625 10.5719V13.125C9.0625 13.5394 9.22712 13.9368 9.52015 14.2299C9.81317 14.5229 10.2106 14.6875 10.625 14.6875C10.8464 14.6878 11.0608 14.6098 11.2302 14.4672C11.3995 14.3246 11.513 14.1267 11.5504 13.9085C11.5878 13.6903 11.5468 13.4659 11.4346 13.275C11.3225 13.0841 11.1464 12.9391 10.9375 12.8656Z" stroke-width="0.3" /></svg></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='nenhum-produto'>Nenhum pedido encontrado para os filtros selecionados.</p>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            <Rodape />
        </section>
    );
}